import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import '../Components/AuditOperationDetail.css';
import Breadcrumbs from '../Components/Breadcrumbs';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import {
  AuditOperationsApiProvider,
  useAuditOperationsApi,
} from '../Services/AuditOperationsApiProvider';
import { formatDateTimeLocal } from '../utils/formatDate';
import { formatOccurrences, humanizeKey } from '../utils/auditChanges';
import { AuditChangesSection, AuditSnapshotSection } from '../Components/AuditChanges';

const parseJsonMaybe = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;

  try {
    return JSON.parse(value);
  } catch (e) {
    try {
      return JSON.parse(value.replace(/'/g, '"'));
    } catch (e2) {
      return null;
    }
  }
};

const formatValue = (value) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch (e) {
    return String(value);
  }
};

function AuditOperationDetail() {
  const { auditOperationId } = useParams();
  const { getAuditOperationById } = useAuditOperationsApi();

  const [auditOperation, setAuditOperation] = useState(null);
  const [error, setError] = useState(null);

  const isNotificationTrigger =
    auditOperation?.operation_type === 'notification:notification.trigger';
  const isConnectionDispatch = auditOperation?.operation_type?.includes('connection_dispatch');
  const isServiceBusDeadMessage = auditOperation?.operation_type?.includes(
    'service_bus_dead_message',
  );
  const isTaskSchedule = auditOperation?.operation_type?.includes('task_schedule');

  const changes = auditOperation?.properties?.changes;
  const entityProperties = auditOperation?.properties?.entity_properties;
  // Present when an update left the entity unreadable — soft-deleted, or moved
  // out of the repository's filter. The before state is the half worth keeping.
  const entityPropertiesBefore = auditOperation?.properties?.entity_properties_before;

  const action = auditOperation?.operation_type?.split('.').pop();
  const snapshotTitle =
    action === 'delete'
      ? 'Deleted Record'
      : action === 'create'
        ? 'Created Record'
        : 'Record Properties';

  // A read row can stand for several accesses: repeats of the same entity by the
  // same user inside the deduplication window are counted, not stored separately.
  const repeatCount = formatOccurrences(auditOperation?.occurrences);

  const connectionDispatchProperties = isConnectionDispatch
    ? auditOperation?.properties || {}
    : null;
  const connectionObjectAttributes =
    connectionDispatchProperties?.connection_object_attribute?.object_attributes;

  const serviceBusMetadata = isServiceBusDeadMessage ? auditOperation.properties : null;
  const serviceBusEventPayload = serviceBusMetadata?.event_payload;

  const taskScheduleProperties = isTaskSchedule ? auditOperation?.properties || {} : null;

  const sentPayload = isConnectionDispatch
    ? parseJsonMaybe(auditOperation?.properties?.sent_payload)
    : null;

  useEffect(() => {
    setError(null);
    setAuditOperation(null);
    getAuditOperationById(auditOperationId)
      .then(setAuditOperation)
      .catch((err) =>
        setError(
          err?.response?.status === 404
            ? 'Audit operation not found'
            : 'Failed to load audit operation',
        ),
      );
  }, [auditOperationId]);

  return (
    <div className="container">
      <Breadcrumbs />

      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          <h2 className="h5-title">Audit operations</h2>
          <div className="radius-yes bg-yes">
            {error && <div>Error: {error}</div>}
            {isNotificationTrigger ? (
              <>
                <div className="detail-item">
                  <label>
                    <strong>Operation Id:</strong>
                  </label>
                  <span>{auditOperation?.id}</span>
                </div>
                <div className="detail-item">
                  <label>
                    <strong>Created At:</strong>
                  </label>
                  <span>
                    {auditOperation?.created_at
                      ? formatDateTimeLocal(auditOperation.created_at)
                      : '-'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>
                    <strong>Operation Type:</strong>
                  </label>
                  <span>{auditOperation?.operation_type}</span>
                </div>
                <div className="detail-item">
                  <label>
                    <strong>Notification Id:</strong>
                  </label>
                  <span>{auditOperation?.properties?.notification_key || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>
                    <strong>Notification Text:</strong>
                  </label>
                  <span>{auditOperation?.properties?.notification_text || '-'}</span>
                </div>
              </>
            ) : (
              <>
                <div className="detail-item">
                  <label>
                    <strong>Operation Id:</strong>
                  </label>
                  <span>{auditOperation?.key}</span>
                </div>
                <div className="detail-item">
                  <label>
                    <strong>Status:</strong>
                  </label>
                  <span
                    className={`status-badge ${auditOperation?.status === 'success' ? 'success' : 'failure'}`}
                  >
                    {auditOperation?.status ? humanizeKey(auditOperation.status) : '-'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>
                    <strong>{repeatCount ? 'First Seen:' : 'Created At:'}</strong>
                  </label>
                  <span>
                    {auditOperation?.created_at
                      ? formatDateTimeLocal(auditOperation.created_at)
                      : '-'}
                  </span>
                </div>
                {repeatCount && (
                  <>
                    <div className="detail-item">
                      <label>
                        <strong>Last Seen:</strong>
                      </label>
                      <span>
                        {auditOperation?.last_seen_at
                          ? formatDateTimeLocal(auditOperation.last_seen_at)
                          : '-'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>
                        <strong>Occurrences:</strong>
                      </label>
                      <span>
                        <span className="occurrences-badge">{repeatCount}</span>
                        <span className="occurrences-note">
                          repeat accesses in this window are counted on one record
                        </span>
                      </span>
                    </div>
                  </>
                )}
                <div className="detail-item">
                  <label>
                    <strong>Operation Type:</strong>
                  </label>
                  <span>{auditOperation?.operation_type}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {!isNotificationTrigger && (
          <div className="radius-yes p-2 bg-yes col-12">
            <h2 className="h5-title">Operation Details</h2>
            <div className="radius-yes bg-yes">
              {isServiceBusDeadMessage ? (
                <>
                  <div className="detail-item">
                    <label>
                      <strong>Event topic:</strong>
                    </label>
                    <span>{serviceBusMetadata?.event_topic || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Event subject:</strong>
                    </label>
                    <span>{serviceBusMetadata?.event_subject || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Delivery count:</strong>
                    </label>
                    <span>{serviceBusMetadata?.delivery_count ?? '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Exception type:</strong>
                    </label>
                    <span>{serviceBusMetadata?.exception_type || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Exception message:</strong>
                    </label>
                    <span>{serviceBusMetadata?.exception_message || '-'}</span>
                  </div>

                  {serviceBusEventPayload && typeof serviceBusEventPayload === 'object' && (
                    <div className="radius-yes bg-yes mt-2 p-2">
                      <h3 className="h5-title mb-2">Event payload</h3>
                      {Object.entries(serviceBusEventPayload).map(([key, value]) => (
                        <div className="detail-item" key={key}>
                          <label>
                            <strong>{humanizeKey(key)}:</strong>
                          </label>
                          <span>{formatValue(value)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : isConnectionDispatch ? (
                <>
                  {Object.entries(connectionDispatchProperties)
                    .filter(([key]) => key !== 'result' && key !== 'connection_object_attribute')
                    .map(([key, value]) => (
                      <div className="detail-item" key={key}>
                        <label>
                          <strong>{humanizeKey(key)}:</strong>
                        </label>
                        <span>{formatValue(value)}</span>
                      </div>
                    ))}
                </>
              ) : isTaskSchedule ? (
                <>
                  <div className="detail-item">
                    <label>
                      <strong>Task Schedule Key:</strong>
                    </label>
                    <span>{taskScheduleProperties?.task_schedule_key || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Assistant Key:</strong>
                    </label>
                    <span>{taskScheduleProperties?.assistant_key || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Execution Key:</strong>
                    </label>
                    <span>{taskScheduleProperties?.execution_key || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Slot Key:</strong>
                    </label>
                    <span>{taskScheduleProperties?.slot_key || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Exception Type:</strong>
                    </label>
                    <span>{taskScheduleProperties?.exception_type || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Exception Message:</strong>
                    </label>
                    <span>{taskScheduleProperties?.exception_message || '-'}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="detail-item">
                    <label>
                      <strong>Operation Request Id:</strong>
                    </label>
                    <span>{auditOperation?.properties?.request_id || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>User Id:</strong>
                    </label>
                    <span>{auditOperation?.user_key || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>User Name:</strong>
                    </label>
                    <span>{auditOperation?.user_name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Record Id:</strong>
                    </label>
                    <span>{auditOperation?.properties?.entity_id || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Record Name:</strong>
                    </label>
                    <span>{auditOperation?.record_name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>Remote IP:</strong>
                    </label>
                    <span>{auditOperation?.properties?.client_ip || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>
                      <strong>User Agent:</strong>
                    </label>
                    <span>{auditOperation?.properties?.user_agent || '-'}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {isConnectionDispatch &&
          Array.isArray(connectionObjectAttributes) &&
          connectionObjectAttributes.length > 0 && (
            <div className="radius-yes p-2 bg-yes col-12">
              <h2 className="h5-title">Object attributes</h2>
              <div className="radius-yes bg-yes">
                <div className="w-100">
                  {connectionObjectAttributes.map((attr, index) => {
                    const sentValue = sentPayload ? sentPayload[attr.attribute_name] : undefined;

                    return (
                      <div className="radius-yes bg-yes mb-2 p-2" key={index}>
                        <div className="detail-item">
                          <label>
                            <strong>Name:</strong>
                          </label>
                          <span>{attr.attribute_name}</span>
                        </div>

                        <div className="detail-item">
                          <label>
                            <strong>Sent Value:</strong>
                          </label>
                          <span>{formatValue(sentValue)}</span>
                        </div>

                        <div className="detail-item">
                          <label>
                            <strong>Type:</strong>
                          </label>
                          <span>{attr.attribute_type}</span>
                        </div>
                        <div className="detail-item">
                          <label>
                            <strong>Required:</strong>
                          </label>
                          <span>{attr.is_required ? 'Yes' : 'No'}</span>
                        </div>
                        <div className="detail-item">
                          <label>
                            <strong>Default Value:</strong>
                          </label>
                          <span>{attr.default_value || '-'}</span>
                        </div>
                        <div className="detail-item">
                          <label>
                            <strong>Parameter Location:</strong>
                          </label>
                          <span>{attr.parameter_location || '-'}</span>
                        </div>
                        <div className="detail-item">
                          <label>
                            <strong>Description:</strong>
                          </label>
                          <span>{attr.description || '-'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

        {!isNotificationTrigger && <AuditChangesSection changes={changes} />}

        {!isNotificationTrigger && (
          <AuditSnapshotSection title={snapshotTitle} snapshot={entityProperties} />
        )}

        {!isNotificationTrigger && (
          <AuditSnapshotSection title="State Before Update" snapshot={entityPropertiesBefore} />
        )}
      </div>
    </div>
  );
}

function AuditOperationsWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <AuditOperationsApiProvider>
        <AuditOperationDetail />
      </AuditOperationsApiProvider>
    </ConfirmationProvider>
  );
}

export default AuditOperationsWrapper;
